import React, { useCallback, useEffect, useRef, useState } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin, Crosshair, Search } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT_CENTER = { lat: 17.385, lng: 78.4867 }; // Hyderabad
const TG_MAP_ID = 'telangana-mart-vendor-picker';

/**
 * PlaceAutocompleteInput — Search box (Google Places Autocomplete) that
 * resolves a selection into a (lat, lng, formatted_address) and calls onPick.
 * Must be rendered as a child of <APIProvider>.
 */
const PlaceAutocompleteInput = ({ onPick, value, onChange }) => {
  const places = useMapsLibrary('places');
  const inputRef = useRef(null);
  const autoRef = useRef(null);

  useEffect(() => {
    if (!places || !inputRef.current) return undefined;
    // Restrict to India by default — the marketplace is Telangana-focused but
    // we keep nationwide allow-listing so vendors can pick warehouses elsewhere.
    const auto = new places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'in' },
      fields: ['formatted_address', 'geometry', 'name', 'address_components'],
    });
    autoRef.current = auto;
    const listener = auto.addListener('place_changed', () => {
      const place = auto.getPlace();
      if (!place?.geometry?.location) return;
      onPick({
        address: place.formatted_address || place.name || '',
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        components: place.address_components || [],
      });
    });
    return () => listener?.remove();
  }, [places, onPick]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search for your shop / warehouse / city…"
        className="pl-9"
        data-testid="location-picker-search"
        autoComplete="off"
      />
    </div>
  );
};

/**
 * Inner map content — handles centering when external coords change and
 * click-to-pin behavior. Must be rendered inside <Map>.
 */
const MapContent = ({ marker, onMarkerDrag, onMapClick, onMyLocation }) => {
  const map = useMap();
  // Recenter the map when an external selection updates the marker.
  useEffect(() => {
    if (!map || !marker) return;
    map.panTo({ lat: marker.lat, lng: marker.lng });
  }, [map, marker?.lat, marker?.lng]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {marker && (
        <AdvancedMarker
          position={{ lat: marker.lat, lng: marker.lng }}
          draggable
          onDragEnd={(e) => {
            const { latLng } = e;
            if (latLng) onMarkerDrag({ lat: latLng.lat, lng: latLng.lng });
          }}
        >
          <div className="w-7 h-7 rounded-full bg-[#ee5e23] border-2 border-white shadow flex items-center justify-center text-white" data-testid="location-picker-marker">
            <MapPin className="w-4 h-4" />
          </div>
        </AdvancedMarker>
      )}
      <button
        type="button"
        onClick={onMyLocation}
        className="absolute bottom-3 right-3 z-10 bg-white shadow rounded-full p-2 hover:bg-gray-50 border border-gray-200"
        title="Use my current location"
        data-testid="location-picker-mylocation"
      >
        <Crosshair className="w-4 h-4 text-[#2c54b2]" />
      </button>
      {/* Overlay catches clicks via map's onClick prop (passed at <Map>); nothing to render here */}
      <span className="hidden" data-onclick-shim onClick={onMapClick} />
    </>
  );
};

/**
 * LocationPicker — search + draggable map pin combo. Calls `onChange` with
 * `{ address, latitude, longitude }` whenever the location changes.
 *
 * Props:
 *  - value: { address?: string, latitude?: number, longitude?: number }
 *  - onChange(next): called with the new value object
 *  - height: optional CSS height for the map (default 280px)
 */
const LocationPicker = ({ value, onChange, height = '280px' }) => {
  const [searchText, setSearchText] = useState(value?.address || '');
  const [marker, setMarker] = useState(
    value?.latitude && value?.longitude
      ? { lat: Number(value.latitude), lng: Number(value.longitude) }
      : null,
  );

  // Keep local searchText in sync when parent resets/updates value.address.
  useEffect(() => {
    setSearchText(value?.address || '');
    if (value?.latitude && value?.longitude) {
      setMarker({ lat: Number(value.latitude), lng: Number(value.longitude) });
    }
  }, [value?.address, value?.latitude, value?.longitude]);

  const emit = useCallback((next) => {
    onChange?.({
      address: next.address ?? value?.address ?? '',
      latitude: next.lat ?? null,
      longitude: next.lng ?? null,
    });
  }, [onChange, value?.address]);

  const handlePick = useCallback((p) => {
    setMarker({ lat: p.lat, lng: p.lng });
    setSearchText(p.address);
    emit({ address: p.address, lat: p.lat, lng: p.lng });
  }, [emit]);

  const handleDrag = useCallback((pos) => {
    setMarker(pos);
    emit({ lat: pos.lat, lng: pos.lng, address: searchText });
  }, [emit, searchText]);

  const handleMapClick = useCallback((e) => {
    const { latLng } = e?.detail || {};
    if (!latLng) return;
    setMarker({ lat: latLng.lat, lng: latLng.lng });
    emit({ lat: latLng.lat, lng: latLng.lng, address: searchText });
  }, [emit, searchText]);

  const handleMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setMarker({ lat: latitude, lng: longitude });
        emit({ lat: latitude, lng: longitude, address: searchText });
      },
      (err) => alert(`Couldn't get your location: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [emit, searchText]);

  if (!GOOGLE_KEY) {
    // Fall back to a plain text input — keeps the form usable when the key is missing.
    return (
      <div>
        <Input
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); emit({ address: e.target.value }); }}
          placeholder="Address"
          data-testid="location-picker-fallback"
        />
        <div className="text-[11px] text-amber-700 mt-1">
          Map picker disabled — REACT_APP_GOOGLE_MAPS_API_KEY is not set.
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_KEY} libraries={['places']}>
      <div className="space-y-2">
        <PlaceAutocompleteInput value={searchText} onChange={setSearchText} onPick={handlePick} />
        <div
          className="relative rounded-md overflow-hidden border border-gray-200"
          style={{ height }}
          data-testid="location-picker-map"
        >
          <Map
            defaultCenter={marker || DEFAULT_CENTER}
            defaultZoom={marker ? 15 : 11}
            mapId={TG_MAP_ID}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            onClick={handleMapClick}
          >
            <MapContent
              marker={marker}
              onMarkerDrag={handleDrag}
              onMapClick={handleMapClick}
              onMyLocation={handleMyLocation}
            />
          </Map>
        </div>
        {marker && (
          <div className="text-[11px] text-gray-500" data-testid="location-picker-coords">
            Pin: <span className="font-mono">{marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}</span>
            {' · '}Drag the pin on the map to refine.
          </div>
        )}
      </div>
    </APIProvider>
  );
};

export default LocationPicker;
